// Comprehensive error handling service for APN
import { toast } from 'react-toastify';

// Error types and categories
export const ERROR_TYPES = {
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  VALIDATION: 'validation',
  NETWORK: 'network',
  FIREBASE: 'firebase',
  UNKNOWN: 'unknown'
};

export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Firebase error code mappings
const FIREBASE_ERROR_MESSAGES = {
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/requires-recent-login': 'Please log out and log back in to continue.',
  'permission-denied': 'You do not have permission to perform this action.',
  'not-found': 'The requested resource was not found.',
  'already-exists': 'This resource already exists.',
  'failed-precondition': 'Operation failed due to current system state.',
  'resource-exhausted': 'Service temporarily unavailable. Please try again.',
  'unauthenticated': 'Please log in to continue.',
  'unavailable': 'Service temporarily unavailable. Please try again.',
  'deadline-exceeded': 'Request timed out. Please try again.',
  'invalid-argument': 'Invalid data provided. Please check your input.',
  'out-of-range': 'Value is out of acceptable range.',
  'unimplemented': 'This feature is not yet available.',
  'internal': 'Internal server error. Please try again later.',
  'data-loss': 'Data corruption detected. Please contact support.',
  'cancelled': 'Operation was cancelled.'
};

// Network error messages
const NETWORK_ERROR_MESSAGES = {
  'ERR_NETWORK': 'Network connection failed. Please check your internet connection.',
  'ERR_TIMEOUT': 'Request timed out. Please try again.',
  'ERR_BLOCKED': 'Request was blocked. Please check your firewall settings.',
  'ERR_FAILED': 'Request failed. Please try again later.'
};

/**
 * Enhanced error logging with context
 * @param {Error} error - The error object
 * @param {object} context - Additional context information
 * @param {string} severity - Error severity level
 */
export const logError = (error, context = {}, severity = ERROR_SEVERITY.MEDIUM) => {
  const errorInfo = {
    message: error.message,
    stack: error.stack,
    code: error.code,
    timestamp: new Date().toISOString(),
    severity,
    context: {
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: context.userId || 'anonymous',
      action: context.action || 'unknown',
      component: context.component || 'unknown',
      ...context
    }
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', errorInfo);
  }

  // In production, you would send this to your logging service
  // Example: sendToLoggingService(errorInfo);
  
  // For critical errors, you might want to alert administrators
  if (severity === ERROR_SEVERITY.CRITICAL) {
    console.error('CRITICAL ERROR:', errorInfo);
    // Example: sendCriticalAlert(errorInfo);
  }
};

/**
 * Categorize error type based on error object
 * @param {Error} error - The error object
 * @returns {string} Error type
 */
export const categorizeError = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;
  
  const code = error.code || '';
  const message = error.message || '';
  
  // Firebase Auth errors
  if (code.startsWith('auth/')) {
    return ERROR_TYPES.AUTHENTICATION;
  }
  
  // Firebase Firestore errors
  if (['permission-denied', 'unauthenticated'].includes(code)) {
    return ERROR_TYPES.AUTHORIZATION;
  }
  
  if (['invalid-argument', 'out-of-range', 'failed-precondition'].includes(code)) {
    return ERROR_TYPES.VALIDATION;
  }
  
  // Network errors
  if (code.startsWith('ERR_') || message.includes('network') || message.includes('fetch')) {
    return ERROR_TYPES.NETWORK;
  }
  
  // Firebase errors
  if (code || error.name === 'FirebaseError') {
    return ERROR_TYPES.FIREBASE;
  }
  
  return ERROR_TYPES.UNKNOWN;
};

/**
 * Get user-friendly error message
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyMessage = (error) => {
  if (!error) return 'An unexpected error occurred.';
  
  const code = error.code || '';
  const message = error.message || '';
  
  // Check Firebase error mappings
  if (FIREBASE_ERROR_MESSAGES[code]) {
    return FIREBASE_ERROR_MESSAGES[code];
  }
  
  // Check network error mappings
  for (const [errorCode, errorMessage] of Object.entries(NETWORK_ERROR_MESSAGES)) {
    if (code.includes(errorCode) || message.includes(errorCode)) {
      return errorMessage;
    }
  }
  
  // Generic error messages based on error type
  const errorType = categorizeError(error);
  
  switch (errorType) {
    case ERROR_TYPES.AUTHENTICATION:
      return 'Authentication failed. Please check your credentials.';
    case ERROR_TYPES.AUTHORIZATION:
      return 'You do not have permission to perform this action.';
    case ERROR_TYPES.VALIDATION:
      return 'Please check your input and try again.';
    case ERROR_TYPES.NETWORK:
      return 'Network error. Please check your connection and try again.';
    case ERROR_TYPES.FIREBASE:
      return 'Service temporarily unavailable. Please try again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Handle error with appropriate user feedback and logging
 * @param {Error} error - The error object
 * @param {object} options - Error handling options
 */
export const handleError = (error, options = {}) => {
  const {
    context = {},
    showToast = true,
    toastType = 'error',
    customMessage = null,
    severity = ERROR_SEVERITY.MEDIUM,
    logError: shouldLog = true
  } = options;
  
  // Log the error
  if (shouldLog) {
    logError(error, context, severity);
  }
  
  // Get user-friendly message
  const message = customMessage || getUserFriendlyMessage(error);
  
  // Show toast notification
  if (showToast) {
    switch (toastType) {
      case 'error':
        toast.error(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      case 'info':
        toast.info(message);
        break;
      default:
        toast.error(message);
    }
  }
  
  return {
    type: categorizeError(error),
    message,
    originalError: error
  };
};

/**
 * Async error handler wrapper for functions
 * @param {Function} fn - The async function to wrap
 * @param {object} context - Context information for error logging
 * @returns {Function} Wrapped function with error handling
 */
export const withErrorHandling = (fn, context = {}) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, { context });
      throw error; // Re-throw to allow caller to handle if needed
    }
  };
};

/**
 * React error boundary helper
 * @param {Error} error - The error object
 * @param {object} errorInfo - React error info
 */
export const handleReactError = (error, errorInfo) => {
  const context = {
    component: 'ErrorBoundary',
    componentStack: errorInfo.componentStack,
    action: 'render'
  };
  
  logError(error, context, ERROR_SEVERITY.HIGH);
  
  // In production, you might want to show a fallback UI
  return {
    hasError: true,
    error,
    errorInfo
  };
};

/**
 * Validation error handler
 * @param {object} validationErrors - Object containing validation errors
 * @param {string} formName - Name of the form being validated
 */
export const handleValidationErrors = (validationErrors, formName = 'form') => {
  const errorMessages = Object.values(validationErrors).filter(Boolean);
  
  if (errorMessages.length > 0) {
    const message = errorMessages.length === 1 
      ? errorMessages[0]
      : `Please fix ${errorMessages.length} errors in the ${formName}.`;
    
    toast.error(message);
    
    // Log validation errors for analysis
    logError(
      new Error('Validation failed'),
      {
        component: formName,
        action: 'validation',
        errors: validationErrors
      },
      ERROR_SEVERITY.LOW
    );
  }
};

/**
 * Success message handler
 * @param {string} message - Success message
 * @param {object} context - Context information
 */
export const handleSuccess = (message, context = {}) => {
  toast.success(message);
  
  // Log successful operations for analytics
  if (process.env.NODE_ENV === 'development') {
    console.log('Success:', message, context);
  }
};

/**
 * Warning message handler
 * @param {string} message - Warning message
 * @param {object} context - Context information
 */
export const handleWarning = (message, context = {}) => {
  toast.warning(message);
  
  // Log warnings
  logError(
    new Error(message),
    { ...context, type: 'warning' },
    ERROR_SEVERITY.LOW
  );
};

/**
 * Info message handler
 * @param {string} message - Info message
 */
export const handleInfo = (message) => {
  toast.info(message);
};

// Export error handling utilities
export default {
  handleError,
  logError,
  categorizeError,
  getUserFriendlyMessage,
  withErrorHandling,
  handleReactError,
  handleValidationErrors,
  handleSuccess,
  handleWarning,
  handleInfo,
  ERROR_TYPES,
  ERROR_SEVERITY
};
